<template>
  <div class="field">
    <transition name="fade">
      <label v-if="title" class="label" :title="description || title">
        {{ label }}
        <template v-if="result"> ({{ result }})</template>
      </label>
    </transition>
    <div v-if="range" ref="widget" :style="{ 'width': width ? width : '100%', marginRight: '14px' }">
      <input/>
      <input/>
    </div>
    <input v-else
           ref="widget"
           type="text"
           :name="name"
           :style="{ 'width': width ? width : '100%', marginRight: '14px' }"/>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  props: {
    label: String,
    description: String,
    dataSource: {
      type: Array,
      default: () => [{}]
    },
    dataTextField: {
      type: String,
      default: 'text'
    },
    dataValueField: {
      type: String,
      default: 'value'
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 10
    },
    smallStep: {
      type: Number,
      default: 1
    },
    largeStep: {
      type: Number,
      default: 5
    },
    width: {
      type: Number
    },
    showButtons: {
      type: Boolean,
      default: false
    },
    enable: {
      type: Boolean,
      default: true
    },
    range: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Object, Array, Number]
    },
    hint: {
      type: Boolean,
      default: false
    }
  },

  data () {
    const value =
      _.isObject(this.value) && this.value.value ? this.range ? _.isArray(this.value.value) ? this.value.value : [this.value.value] : this.value.value : _.isNumber(this.value) ? this.value : this.getMinFromField();

    const config = {
      min: this.dataSource ? this.getMinFromField() : this.min,
      max: this.dataSource ? this.getMaxFromField() : this.max,
      smallStep: this.smallStep,
      largeStep: this.largeStep,
      showButtons: this.showButtons,
      enable: this.enable,
      value,
      change: this.onChange
    };

    return {
      title: this.label,
      config,
      widget: undefined
    };
  },

  computed: {
    result () {
      if (this.widget && this.hasValue() && _.size(this.dataSource)) {
        const value = this.widget.value();
        return String(this.range ? _.map(value, v => this.template[v]).join(' : ') : this.template[value]);
      }
      return '';
    },

    template () {
      if (this.dataSource) {
        return _.reduce(this.dataSource, (acc, data) => {
          acc[data[this.dataValueField]] = data[this.dataTextField];
          return acc;
        }, {});
      }
      return {};
    }
  },

  mounted () {
    this.widget = this.range ? new kendo.ui.RangeSlider(this.$refs.widget, this.config) : new kendo.ui.Slider(this.$refs.widget, this.config);
    this.onChange({ value: this.widget.value() });
  },

  destroyed () {
    this.widget.destroy();
  },

  methods: {
    onChange (e) {
      const data = this.formatValue(e.value);
      this.$emit('input', data);
      this.$emit('change', data);
    },

    hasValue () {
      const value = this.widget.value();
      return (_.isArray(value) ? _.size(value) : !!String(value));
    },

    formatValue (value) {
      return {
        metadata: {
          name: this.name,
          text: this.dataTextField,
          value: this.dataValueField,
          properties: Object.keys(this.dataSource[0] || {})
        },
        text: this.range ? _.map(value, v => this.template[v]) : this.template[value],
        value: value
      };
    },

    getMaxFromField () {
      return _.maxBy(this.dataSource, d => d[this.dataValueField])[this.dataValueField];
    },

    getMinFromField () {
      return _.minBy(this.dataSource, d => d[this.dataValueField])[this.dataValueField];
    },

    getStepSizeFromField () {
      return Math.floor(_.size(this.dataSource) / 2);
    }
  },

  watch: {
    value (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal)) {
        this.widget.value(_.isObject(newVal) ? newVal.value : newVal);
      }
    },

    dataSource (val) {
      if (_.size(val)) {
        this.widget.options.max = val ? this.getMaxFromField() : this.max;
        this.widget.options.min = val ? this.getMinFromField() : this.min;
        this.widget.value(this.range ? [this.widget.options.min, this.widget.options.max] : this.widget.options.max);
        this.onChange({ value: this.widget.value() });
      }
    },

    dataTextField (val) {
      this.widget.setOptions({ dataTextField: val });
    },

    dataValueField (val) {
      this.widget.setOptions({ dataValueField: val });
    },

    enable (val) {
      this.widget.setOptions({ enable: val });
      this.widget.enable(val);
    }
  }
};
</script>

<style lang="scss" scoped>
  $labelColor: #888;

  .label {
    color: $labelColor !important;
    font-size: 13px !important;
    text-transform: uppercase;
  }

  // Animations
  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s
  }

  .fade-enter, .fade-leave-active {
    opacity: 0
  }
</style>
